import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AboutPage.css';
import { PageHero } from '../../components/PageHero/PageHero';
import warehousePic from '../../images/hero_warehouse.jpg';
import teamPic from '../../images/team-team.jpg';

const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PILLARS = [
  {
    icon: <ShieldIcon />,
    title: 'Licensed & Compliant',
    body: 'FMCSA licensed, bonded, and fully insured. MC# and DOT# documentation available on request. Every carrier in our network is vetted for compliance before a single load moves.',
  },
  {
    icon: <ClockIcon />,
    title: 'Proactive, Not Reactive',
    body: 'We notify you before delays affect your customer — not after. Every active shipment is tracked and updated at every milestone. You have one point of contact, not a call center.',
  },
  {
    icon: <MapPinIcon />,
    title: 'SE US Specialists',
    body: "Our network is built for Southeast, Gulf Coast, and East Coast lanes. We're not promising coast-to-coast coverage we can't deliver — we specialize in the corridors that matter to you.",
  },
];

const STATS = [
  { num: '12+', label: 'Years Combined Experience' },
  { num: '96%', label: 'On-Time Delivery Rate' },
  { num: '48h', label: 'Average Onboarding' },
  { num: '99%', label: 'Order Fill Accuracy' },
];

export const AboutPage = () => {
  const storyRef = useRef<HTMLElement>(null);
  const pillarsRef = useRef<HTMLElement>(null);
  const numbersRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const refs = [storyRef, pillarsRef, numbersRef];
    const observers = refs.map(ref => {
      const obs = new IntersectionObserver(
        entries => entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
        }),
        { threshold: 0.08 }
      );
      ref.current?.querySelectorAll<HTMLElement>('.rn-reveal').forEach(el => obs.observe(el));
      return obs;
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <>
      <Helmet>
        <title>About Renuit Logistics</title>
        <meta name="description" content="Licensed freight broker and 3PL based in St. Petersburg, FL. 12+ years moving SE US and Gulf Coast freight lanes with 96% on-time delivery." />
        <link rel="canonical" href="https://www.renuit.pro/about" />
        <meta property="og:title" content="About Renuit Logistics" />
        <meta property="og:description" content="Licensed freight broker and 3PL based in St. Petersburg, FL. 12+ years moving SE US and Gulf Coast freight lanes with 96% on-time delivery." />
        <meta property="og:url" content="https://www.renuit.pro/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content="About Renuit Logistics" />
        <meta name="twitter:description" content="Licensed freight broker and 3PL based in St. Petersburg, FL. 12+ years moving SE US and Gulf Coast freight lanes with 96% on-time delivery." />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
      </Helmet>

      <PageHero
        image={warehousePic}
        tag="Our Story"
        heading="Built by Operators, Not Brokers"
        subheading="Renuit Logistics was founded to fix what every shipper eventually experiences — a freight partner that disappears when things go sideways."
      />

      {/* Founding Story */}
      <section className="rn-about-story" ref={storyRef}>
        <div className="rn-about-story__inner">
          <div className="rn-about-story__text rn-reveal">
            <span className="cs-topper">Who We Are</span>
            <h2 className="cs-title">We Own Our Infrastructure. Most Don't.</h2>
            <p className="rn-about-story__body">
              Renuit Logistics started from a simple observation: most logistics providers are brokers
              with a website. They take your load, hand it to a carrier they've never met, and check out
              until something goes wrong. We built Renuit to be the opposite.
            </p>
            <p className="rn-about-story__body">
              Based in St. Petersburg, FL, we serve freight and distribution clients throughout the
              Southeast U.S., Gulf Coast, and East Coast corridors. Our team brings over 12 years of
              combined logistics experience — not in theory, but moving actual freight on actual lanes.
            </p>
            <p className="rn-about-story__body">
              We operate our own climate-controlled warehouse infrastructure in the Tampa Bay area,
              which means your cargo never sits in a facility we can't control. Every client has a
              named account manager who knows their lanes, their volume, and their standards.
            </p>
            <p className="rn-about-story__body">
              The name Renuit means reconnection — which is exactly what we do for shippers who've been let down.
            </p>
          </div>
          <div className="rn-about-story__img-col rn-reveal rn-reveal-delay-2">
            <div className="rn-about-story__img-wrap">
              <img
                src={teamPic}
                alt="Renuit logistics team"
                className="rn-about-story__img"
                loading="lazy"
                decoding="async"
              />
              <div className="rn-about-story__badge">
                <span className="rn-about-story__badge-num">12+</span>
                <span className="rn-about-story__badge-label">Years in<br />Logistics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Pillars */}
      <section className="rn-about-pillars" ref={pillarsRef}>
        <div className="rn-about-pillars__inner">
          <div className="rn-about-pillars__header rn-reveal">
            <span className="cs-topper" style={{ color: 'var(--pr-accent)' }}>How We Operate</span>
            <h2 className="rn-about-pillars__heading">Three Things We Never Compromise On</h2>
          </div>
          <div className="rn-about-pillars__grid">
            {PILLARS.map(({ icon, title, body }, i) => (
              <div key={title} className={`rn-about-pillars__card rn-reveal rn-reveal-delay-${i + 1}`}>
                <div className="rn-about-pillars__icon">{icon}</div>
                <h3 className="rn-about-pillars__card-title">{title}</h3>
                <p className="rn-about-pillars__card-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section className="rn-about-numbers" ref={numbersRef}>
        <div className="rn-about-numbers__inner">
          {STATS.map(({ num, label }, i) => (
            <div key={label} className={`rn-about-numbers__stat rn-reveal rn-reveal-delay-${i % 3 + 1}`}>
              <span className="rn-about-numbers__num">{num}</span>
              <span className="rn-about-numbers__label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="rn-about-cta">
        <div className="rn-about-cta__inner">
          <h2 className="rn-about-cta__heading">Ready to Talk Freight?</h2>
          <p className="rn-about-cta__sub">No commitment required. We'll respond within 24 hours.</p>
          <div className="rn-about-cta__actions">
            <Link to="/quote" className="cs-button-solid">Get a Quote</Link>
            <Link to="/services" className="rn-about-cta__secondary">View Services →</Link>
          </div>
        </div>
      </section>
    </>
  );
};
